import React from "react";
import { Table, Select, Checkbox } from "antd";
import { useSelector } from "react-redux";
import "./index.css";

const { Option } = Select;

const InvoicesTable = () => {
  const { invoicesData, loading } = useSelector(
    (state) => state.invoicesReducer
  );

  // Списък с всички доставчици за филтъра
  const supplierNames = Array.from(
    new Set((invoicesData || []).map((inv) => inv.supplierName))
  );

  const supplierOptions = supplierNames.map((name) => ({
    text: name,
    value: name,
  }));

  // Списък с всички статуси за филтъра
  const statusNames = Array.from(
    new Set((invoicesData || []).map((inv) => inv.status))
  );

  const statusOptions = statusNames.map((name) => ({
    text: name,
    value: name,
  }));

  const columns = [
    {
      title: "Дата",
      dataIndex: "invoiceDate",
      key: "invoiceDate",
      render: (date) => new Date(date).toLocaleString("bg-BG"),
      sorter: (a, b) => new Date(a.invoiceDate) - new Date(b.invoiceDate),
      defaultSortOrder: "descend",
      width: "20%",
    },

    {
      title: "Доставчик",
      dataIndex: "supplierName",
      key: "supplierName",
      align: "center",
      filters: supplierOptions,
      width: "25%",
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }) => (
        <div style={{ padding: 8 }}>
          <Select
            showSearch
            placeholder="Избери доставчик"
            value={selectedKeys[0]}
            onChange={(value) => {
              setSelectedKeys(value ? [value] : []);
              // apply immediately when selection changes
              confirm();
            }}
            style={{ width: 200, marginBottom: 8, display: "block" }}
            filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            allowClear
          >
            {supplierNames.map((name) => (
              <Option key={name} value={name}>
                {name}
              </Option>
            ))}
          </Select>
          {/* No footer buttons: applying immediately on selection change */}
        </div>
      ),
      filterIcon: (filtered) => (
        <span
          role="img"
          aria-label="search"
          style={{ color: filtered ? "#1890ff" : undefined }}
        >
          🔍
        </span>
      ),
      onFilter: (value, record) => record.supplierName === value,
    },
    {
      title: "Номер",
      dataIndex: "invoiceNumber",
      key: "invoiceNumber",
      sorter: (a, b) => {
        const av = a && a.invoiceNumber;
        const bv = b && b.invoiceNumber;

        // Treat null/undefined as empty string and push them to the end
        if (av == null && bv == null) return 0;
        if (av == null) return 1; // a after b
        if (bv == null) return -1; // a before b

        // If both are numbers (or numeric strings), compare numerically
        const an = Number(av);
        const bn = Number(bv);
        const anIsNum = !Number.isNaN(an);
        const bnIsNum = !Number.isNaN(bn);
        if (anIsNum && bnIsNum) return an - bn;

        // Fallback: compare as strings using localeCompare
        return String(av).localeCompare(String(bv), undefined, {
          numeric: true,
        });
      },
      width: "15%",
    },
    {
      title: "Статус",
      dataIndex: "status",
      key: "status",
      width: "10%",
      filters: statusOptions,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm }) => (
        <div style={{ padding: 8 }}>
          {statusNames.map((name) => {
            const checked = (selectedKeys || []).includes(name);
            return (
              <div
                key={name}
                style={{
                  display: "flex",
                  alignItems: "center",
                  // width: "100%",
                  // padding: "6px 0",
                }}
              >
                <Checkbox
                  checked={checked}
                  onChange={(e) => {
                    const next = e.target.checked
                      ? [...(selectedKeys || []), name]
                      : (selectedKeys || []).filter((k) => k !== name);
                    setSelectedKeys(next);
                    // apply immediately when a checkbox is toggled
                    confirm();
                  }}
                >
                  {name}
                </Checkbox>
              </div>
            );
          })}
        </div>
      ),
      filterIcon: () => (
        <span role="img" aria-label="filter">
          🔍
        </span>
      ),
      onFilter: (value, record) => record.status === value,
    },
    {
      title: "Преглед",
      dataIndex: "preview",
      key: "preview",
      width: "10%",
      render: (preview) => {
        if (preview == null) return null;

        // If preview is an array of URLs, render multiple links: Виж 1, Виж 2, ...
        if (Array.isArray(preview)) {
          return (
            <>
              {preview.map((url, idx) => (
                // show comma between items except before the first
                <span key={idx}>
                  {idx > 0 ? ", " : ""}
                  <a href={url} target="_blank" rel="noopener noreferrer">
                    {`Снимка ${idx + 1}`}
                  </a>
                </span>
              ))}
            </>
          );
        }

        // If it's a single URL string, render one link
        if (typeof preview === "string") {
          return (
            <a href={preview} target="_blank" rel="noopener noreferrer">
              Виж
            </a>
          );
        }

        // Fallback: try to coerce to string and render
        try {
          const url = String(preview);
          return (
            <a href={url} target="_blank" rel="noopener noreferrer">
              Виж
            </a>
          );
        } catch (e) {
          return null;
        }
      },
    },
  ];

  return (
    <div style={{ marginTop: 24 }}>
      <Table
        rowKey="invoiceNumber"
        className="invoice-table"
        rowClassName={(record) =>
          record &&
          record.status &&
          String(record.status).toLowerCase() === "изтрита"
            ? "invoice-deleted"
            : ""
        }
        loading={loading}
        dataSource={invoicesData}
        columns={columns}
        pagination={false}
      />
    </div>
  );
};

export default InvoicesTable;
